import { Link, useNavigate } from "react-router-dom";
import { getPlatformNotificationTarget } from "../../../utils/notificationNavigation";
import { useNotifications } from "../../../hooks/notification/useNotifications";
import { useMarkAsRead } from "../../../hooks/notification/useMarkAsRead";
import { useMarkAllAsRead } from "../../../hooks/notification/useMarkAllAsRead";
import { useModuleEnabled } from "../../../hooks/gym/useModuleEnabled";
import { NotificationTypeIcon } from "./notificationIcons";
import type { NotificationOutput } from "../../../hooks/requests/notification/notification";

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)}d`;
  return new Date(dateStr).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
  });
}

interface NotificationDropdownProps {
  onClose: () => void;
}

export const NotificationDropdown = ({
  onClose,
}: NotificationDropdownProps) => {
  const navigate = useNavigate();
  const { data, isLoading } = useNotifications({ limit: 10, offset: 0 });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const inventoryEnabled = useModuleEnabled("inventory");

  const allNotifications = data?.notifications ?? [];
  const notifications = inventoryEnabled
    ? allNotifications
    : allNotifications.filter((n) => n.type !== "LOW_STOCK");
  const hasUnread = notifications.some((n) => !n.readAt);

  const handleItemClick = (notification: NotificationOutput) => {
    if (!notification.readAt) {
      markAsRead.mutate(notification.publicId);
    }
    const target = getPlatformNotificationTarget(
      notification.type,
      notification.metadata,
    );
    if (target) {
      onClose();
      navigate(target);
    }
  };

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-global-primary border border-global-primary rounded-lg shadow-global-card z-[60] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-global-primary">
        <span className="text-size-xs font-semibold text-global-title">
          Notificaciones
        </span>
        {hasUnread && (
          <button
            type="button"
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            className="text-size-2xs text-global-accent hover:text-global-title-accent transition-colors disabled:opacity-50"
          >
            Marcar todas como leídas
          </button>
        )}
      </div>

      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-global-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-global-muted">
            <svg
              className="w-8 h-8 mb-2 opacity-40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <span className="text-size-2xs">Sin notificaciones</span>
          </div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.publicId}
              type="button"
              onClick={() => handleItemClick(notification)}
              className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-global-overlay-secondary ${
                !notification.readAt ? "bg-global-overlay" : ""
              }`}
            >
              <div className="mt-0.5">
                <NotificationTypeIcon type={notification.type} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-size-2xs font-medium text-global-title truncate">
                    {notification.title}
                  </span>
                  {!notification.readAt && (
                    <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--color-error-text)]" />
                  )}
                </div>
                <p className="text-size-2xs text-global-secondary truncate mt-0.5">
                  {notification.message}
                </p>
                <span className="text-[10px] text-global-muted mt-0.5 block">
                  {formatRelativeTime(notification.createdAt)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>

      <Link
        to="/notifications"
        onClick={onClose}
        className="block text-center text-size-2xs text-global-accent hover:text-global-title-accent py-2 border-t border-global-primary transition-colors"
      >
        Ver todas &rarr;
      </Link>
    </div>
  );
};
